<script setup>
import AppLayout from '@/Layouts/Layout';
import { InformationCircleIcon } from '@heroicons/vue/solid';
import { ref } from 'vue';
import Popper from 'vue3-popper';

const props = defineProps({
    userAchievements: Object
});

const achievementDetails = ref({});
const showModal = ref(false);
const hover = ref(false);

const calculatePercentage = (progress) => {
    if (!progress) return 0;
    const [achieved, total] = progress.split('/').map(Number);
    return total > 0 ? (achieved / total) * 100 : 0;
};

const calculateStrokeDashoffset = (percentage) => {
    const r = 55;
    const c = 2 * Math.PI * r;
    return c - (percentage / 100) * c;
};

async function fetchAchievementDetail(id) {
    try {
        const response = await axios.get(`/achievement/${id}`);
        achievementDetails.value = response.data;
        this.showModal = true;
    } catch (error) {
        console.error("Error fetching achievement details:", error);
    }
};

function closeModal() {
    showModal.value = false;
};

function getStrokeColor(level) {
    switch (level) {
        case 'Gold':
            return '#FFD700'; 
        case 'Silver':
            return '#C0C0C0'; 
        case 'Bronze':
            return '#a05822'; 
        default:
            return '#fcea0d';
    }
}

function getLevelBasedColor(percentage, level){
    if(percentage > 0 && level == 'Gold') {
        return 'bg-gold';
    } else if(percentage > 0 && level == 'Bronze') {
        return 'bg-bronze';
    } else if(percentage > 0 && level == 'Silver') {
        return 'bg-silver';
    } else {
        return 'bg-default';
    }
}

</script>

<template>
    <app-layout :racingstars="$page.props.racingstars" :jetstream="$page.props.jetstream" :user="$page.props.user" title="Achievements">
        <template #header>
            Achievements
        </template>
        <div class="dark:bg-gray-800 lg:dark:bg-gray-900 text-white lg:py-5 lg:px-6 lg:shadow transition duration-500 ease-in-out bg-white rounded-lg lg:border-2 border-gray-600 p-1 lg:p-4 m-4">
            <p class="text-center text-2xl font-semibold transition duration-500 ease-in-out text-white sm:text-gray-600 sm:dark:text-white col-span-1 md:col-span-2 pb-0 sm:pb-4">
                Achievements
            </p>
            <div class="col-sm transition-all duration-900 ease-in-out grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <button v-for="achievement in userAchievements" :key="achievement.id" @click="fetchAchievementDetail(achievement.achievement_badge_id)" class="justify-center h-full rounded-md border transition duration-100 ease-in-out animate-flow" :class="getLevelBasedColor(calculatePercentage(achievement.progress), achievement.level)">
                    <div class=" flex flex-col mx-3 my-6">
                        <div class="flex flex-col mx-auto justify-center">
                            <div class="flex items-center pb-4">
                                <p class="text-white mx-auto px-2 font-semibold w-full text-center">{{ achievement.achievement_badge.name }}</p>
                                <Popper placement="bottom" arrow="true" style="--popper-theme-background-color:#030712;--popper-theme-background-color-hover:#030712;" class="cursor-pointer !border-0 !m-0">
                                    <InformationCircleIcon class="text-white h-4 w-4 my-auto mr-1"/>
                                    <template #content>
                                        <div class="hidden md:flex flex-col bg-gray-950 my-2 p- rounded-b text-xs text-white w-full">
                                            <div class="flex mb-2">
                                                <p class="my-auto ml-1 font-medium text-sm">{{ achievement.achievement_badge.description }}</p>
                                            </div>
                                        </div>
                                    </template>
                                </Popper>
                            </div>
                            <div class="flex flex-col mx-auto justify-center">
                                <span class="relative">
                                    <img class="w-36 h-36 mx-auto" :src="achievement.achievement_badge.image"/>
                                </span>
                                <div class="h-2 bg-gray-500 rounded-md w-full my-3" v-if="Object.keys(achievement).length">
                                    <div class="h-2 bg-white rounded-md" :style="{ width: (calculatePercentage(achievement?.progress) || 0) + '%' }"></div>
                                    <span>{{ achievement?.progress }}</span>
                                    <!-- <span v-if="userAchievementsData[achievement.name][level]">{{ userAchievementsData[achievement.name].level }}</span> -->
                                </div>
                                <div v-else>
                                    Loading...
                                </div>
                            </div>
                        </div>
                    </div>
                </button>
            </div>
        </div>
        <!-- Modal for displaying achievement details -->
        <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div class="bg-white rounded-lg w-[300px]">
                <div class="flex justify-end">
                    <button @click="closeModal()" class="text-black px-4 py-2 rounded">
                        <svg viewBox="0 0 24 24" height="26" width="26" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                            <g id="SVGRepo_iconCarrier">
                                <rect width="24" height="24" fill="white"></rect>
                                <path d="M7 17L16.8995 7.10051" stroke="#000" stroke-linecap="round" stroke-linejoin="round"></path>
                                <path d="M7 7.00001L16.8995 16.8995" stroke="#000" stroke-linecap="round" stroke-linejoin="round"></path>
                            </g>
                        </svg>
                    </button>
                </div>
                <div class="pt-4 px-8 pb-8">
                    <h2 class="text-2xl text-center font-bold mb-4">{{ achievementDetails.achievement_badge.name }}</h2>
                    <p class="text-center mb-2">{{ achievementDetails.achievement_badge.description }}</p>
                    <div class="flex justify-center items-center">
                        <svg class="progress-circle" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="55" class="bg-circle" />
                            <circle v-if="calculatePercentage(achievementDetails.progress) > 0" cx="60" cy="60" r="55" class="progress-bar" :style="{ strokeDasharray: 2 * Math.PI * 55, strokeDashoffset: calculateStrokeDashoffset(calculatePercentage(achievementDetails.progress)), stroke: getStrokeColor(achievementDetails.level) }" stroke-linecap="round" />
                            <image x="27.5" y="27.5" height="65" width="65" :xlink:href="achievementDetails.achievement_badge.image" clip-path="circle(50%)" />
                        </svg>
                    </div>
                    <p class="text-center mt-2">{{ achievementDetails.progress }}</p>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<style scoped>
    .progress-circle {
        width: 170px;
        height: 170px;
    }
    .bg-circle {
        fill: none;
        stroke: #e6e6e6;
        stroke-width: 10;
    }
    .progress-bar {
        fill: none;
        stroke: #fcea0d;
        stroke-width: 10;
        stroke-dasharray: 345.6;
        stroke-dashoffset: 0;
        transition: stroke-dashoffset 0.35s;
        transform: rotate(-90deg);
        transform-origin: 50% 50%;
    }
    .bg-silver{
        border-color: #ffffff !important;
        background: linear-gradient(110deg, rgba(143,143,143,1) 12%, rgba(171,171,171,1) 34%, rgba(215,215,215,1) 74%);
    }
    .bg-bronze{
        border-color: #ffce9e !important;
        background: linear-gradient(110deg, rgba(142,99,71,1) 5%, rgba(172,120,79,1) 27%, rgba(219,169,122,1) 57%);
    }
    .bg-gold{
        border-color: rgba(229, 197, 105, 1) !important;
        background: linear-gradient(329deg, rgba(196, 149, 66, 1) 4%, rgba(196, 149, 66, 1) 31%, rgba(229, 197, 105, 1) 59%);
    }
    .bg-default{
        border-color: #9B9FA2 !important;
        background: transparent;
        opacity: 0.5;
    }
</style>
